/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Button, CardLayered, CardLayeredVariations, Heading, size, tags, Text, Types } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { bestOfFilters, getDirection, getSubDirectoryLocale, useGetBreakpoint } from '../../modules/utils/helpers';
import {
  BESTOF_RESULT_LIMIT,
  IMAGE_URL_DOMAIN,
  searchResultsPageUrl,
  TRACKING_CONSTANT,
} from '../../modules/utils/constants/constants';
import { StyledBestOfComponent } from './BestOfComponent.styles';
import Glide from '@glidejs/glide';
import { useLazyQuery } from '@apollo/client';
import { searchBestRestaurantsByGeolocation } from '@marriott/mi-rnb-graphql';
import { PageParamsContext } from '../../modules/context/PageContext';
import Cookies from 'js-cookie';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { SkeletonCarouselLoader } from '../BestOfComponentSkeleton/BestOfComponentSkeleton';
import bestOfComponentMock from './__mock__/bestOfComponentMock.json';

const { NEXT_PUBLIC_AEM_SITE } = process.env;

declare global {
  interface Window {
    google: any;
  }
}

export const BestOfComponent = (props: any) => {
  const glideRef = useRef<HTMLDivElement | any>(null);
  const [isTabletViewPort, setIsTabletViewPort] = useState(useGetBreakpoint() !== 'mobile');
  const { currentLocale } = useContext(PageParamsContext);
  const pageContext = useContext(createContext<any>({}));
  const requestId = useMemo(() => {
    return pageContext.requestId ? pageContext.requestId : `${Date.now()}`;
  }, [pageContext]);
  const sessionID = Cookies.get('sessionID');
  const currentTimestamp = Date.now();
  const currentDateTimeStamp = new Date(currentTimestamp).getTime();
  const [bestOfRestaurants, setBestOfRestaurants] = useState<any>([]);
  const cardsPerView = isTabletViewPort ? 2 : 1;
  const [searchTerm, setSearchTerm] = useState('');
  const subLocale = getSubDirectoryLocale(currentLocale || 'en-US');
  const [isLoader, setIsLoader] = useState(true);
  const GOOGLE_MAP_API_KEY: any = useClientEnvVarsStore.getState().envVarsObject['GOOGLE_MAP_API_KEY'];
  const { CITY_PAGE, BEST_OF_COMPONENT, INTERNAL_LINK } = TRACKING_CONSTANT;

  const [getBestOfRestaurants] = useLazyQuery(searchBestRestaurantsByGeolocation);

  useEffect(() => {
    function handleResize() {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      setIsTabletViewPort(useGetBreakpoint() !== 'mobile');
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (props?.isAuthorMode) {
      console.log('123 in authormode useEffect - bestof', props.isAuthorMode);
      setIsLoader(false);
      setBestOfRestaurants(bestOfComponentMock?.data?.searchRestaurantsByGeoLocation?.edges);
    }
  }, [props?.isAuthorMode]);

  useEffect(() => {
    if (props?.latitude && props?.longitude && !props.isAuthorMode) {
      console.log('123 in UXL useEffect - bestof', props.isAuthorMode);
      setIsLoader(true);
      loadGoogleMap();
      getBestOfRestaurants({
        fetchPolicy: 'network-only',
        ssr: false,
        errorPolicy: 'all',
        variables: {
          search: {
            latitude: props?.latitude,
            longitude: props?.longitude,
            facets: {
              terms: bestOfFilters(props),
              ranges: [],
            },
          },
          limit: BESTOF_RESULT_LIMIT,
        },
        context: {
          headers: {
            'accept-language': currentLocale?.replace('_', '-') ?? 'en-US',
            'x-request-id': requestId,
            'x-b3-traceId': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
            'x-b3-spanId': requestId !== '' ? requestId : `${currentDateTimeStamp}`,
            'correlation-id': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
          },
        },
        onCompleted: data => {
          setIsLoader(false);
          if (data?.searchRestaurantsByGeolocation?.edges && data?.searchRestaurantsByGeolocation?.edges?.length > 0) {
            setBestOfRestaurants(data?.searchRestaurantsByGeolocation?.edges);
          } else {
            setBestOfRestaurants([]);
            setSearchTerm('');
          }
        },
        onError: () => {
          setIsLoader(false);
          setBestOfRestaurants([]);
          setSearchTerm('');
        },
      });
    } else {
      // If no coordinates provided, don't show loader and mark geocoding as complete
      setIsLoader(false);
    }
  }, [props?.latitude, props?.longitude, props?.isAuthorMode]);

  const ArrowDisabler = useCallback(
    (Glide: any, Components: any) => {
      return {
        mount() {
          // Only in effect when rewinding is disabled
          // if (Glide.settings.rewind) {
          //   return;
          // }
          Glide.on(['mount.after', 'run'], () => {
            const component = document?.getElementById(`${props?.componentId}`);
            if (component) {
              component.style.visibility = 'visible';
            }

            // Filter out arrows_control
            for (const controlItem of Components.Controls.items) {
              if (controlItem.className !== 'glide__arrows') {
                continue;
              }

              // Set left arrow state
              const left = controlItem.querySelector('.glide__arrow--left');
              const direction = getDirection();
              if (left) {
                if (direction === 'rtl' && Glide.index === Components.Sizes.length - Glide.settings.perView) {
                  left.classList.add('.is-disabled'); // Disable on first slide
                } else if (direction === 'ltr' && Glide.index === 0) {
                  left.classList.add('.is-disabled'); // Disable on first slide
                } else {
                  left.classList.remove('.is-disabled'); // Enable on other slides
                }
              }

              // Set right arrow state
              const right = controlItem.querySelector('.glide__arrow--right');
              if (right) {
                if (direction === 'rtl' && Glide.index === 0) {
                  right.classList.add('.is-disabled'); // Disable on last slide
                } else if (direction === 'ltr' && Glide.index === Components.Sizes.length - Glide.settings.perView) {
                  right.classList.add('.is-disabled');
                } else {
                  right.classList.remove('.is-disabled'); // Disable on other slides
                }
              }
            }
          });
        },
      };
    },
    [props?.componentId]
  );

  useEffect(() => {
    const carouselRootSelector = `#${props?.componentId}`;
    const direction = getDirection();
    if (document?.querySelector(carouselRootSelector)) {
      try {
        props?.componentId &&
          new Glide(carouselRootSelector, {
            type: 'slider',
            bound: true,
            perView: cardsPerView,
            startAt: 0,
            rewind: false,
            direction: direction,
            gap: 24,
            dragThreshold: false,
            keyboard: false,
          }).mount({ ArrowDisabler });
      } catch (error) {
        // log.error(`Error in initializing Glide for ${componentId}`, error);
      }
    }
  }, [props?.componentId, bestOfRestaurants, ArrowDisabler, cardsPerView]);

  const loadGoogleMap = async () => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_API_KEY}`;
    script.id = 'google-map-script';
    script.defer = true;

    // Append the 'script' element to 'head'
    document.head.appendChild(script);
    script.addEventListener('load', function (e) {
      e.preventDefault();
      if (window?.google) {
        const event = new CustomEvent('GOOGLE_MAP_SCRIPT_LOADED', { detail: { loaded: true } });
        document.dispatchEvent(event);

        const geocoder = new window.google.maps.Geocoder();
        const latlng = { lat: props?.latitude, lng: props?.longitude };

        // using Goggle Maps API reverse geocoding service to convert latlng to location details
        geocoder.geocode({ location: latlng }, (results: any, status: any) => {
          if (status === 'OK') {
            console.log('123123 - results', results[0]);
            if (results[0]) setSearchTerm(results[0].formatted_address);
          } else setSearchTerm('');
        });
      }
    });
  };

  const getDynamicMedia = (item: any) => {
    if (item?.node?.media?.imageConnection?.edges[0]?.node?.imageUrls?.wideHorizontal) {
      const renditions = {
        altText: item?.node?.media?.imageConnection?.edges[0]?.node?.alternateDescription,
        assetPath: `${IMAGE_URL_DOMAIN}${item?.node?.media?.imageConnection?.edges[0]?.node?.imageUrls?.wideHorizontal}`,
        dynamic: false,
        renditions: [],
      };
      return renditions;
    } else if (
      props?.dynamicMediaWide &&
      (props?.dynamicMediaWide?.damPath !== '' || props?.dynamicMediaWide?.damPath !== null)
    ) {
      return props?.dynamicMediaWide;
    } else return undefined;
  };

  const getViewAllLink = () => {
    const termValue = searchTerm || props?.city || '';
    const link = `${subLocale}${searchResultsPageUrl}?term=${encodeURIComponent(termValue)}&lat=${
      props?.latitude
    }&long=${props?.longitude}`;

    const currentParams = new URLSearchParams(window.location.search);
    if (props?.city) currentParams.set('city', props?.city);
    if (props?.cuisine) currentParams.set('cuisine', props?.cuisine);
    if (props?.experiences) currentParams.set('experiences', props?.experiences);
    if (props?.venueType) currentParams.set('venueType', props?.venueType);
    if (props?.seating) currentParams.set('seating', props?.seating);

    return `${link}&${currentParams.toString()}`;
  };

  if (isLoader) {
    return <SkeletonCarouselLoader />;
  }

  return bestOfRestaurants?.length > 0 ? (
    <StyledBestOfComponent className={clsx('py-5', props?.styleclass)}>
      <div className="container">
        <div className="d-flex flex-column">
          <div
            className={clsx(
              'd-flex justify-content-between align-items-baseline',
              !isTabletViewPort ? 'flex-column' : 'flex-row '
            )}
          >
            <Heading
              titleText={props?.title}
              variation={Types.headingType.title}
              fontSize={Types.size.small}
              customClass={clsx('text-left')}
            />

            {props?.ctaLabel && (
              <Button
                isLink={true}
                href={getViewAllLink()}
                target={props?.openInNewTab ? '_blank' : '_self'}
                className="m-link-tertiary-button mb-3 best-of-viewall-link"
                aria-label={props?.ctaLabel}
                custom_click_track_value={`${CITY_PAGE}-${BEST_OF_COMPONENT}|${props?.ctaLabel}|${INTERNAL_LINK}`}
              >
                {props?.ctaLabel}
              </Button>
            )}
          </div>
          {props?.description && (
            <Text
              copyText={props?.description}
              fontSize={Types.size.small}
              element={Types.tags.span}
              customClass={'t-font-s mb-3'}
            />
          )}

          <div id={props?.componentId} className="glide col-12 px-0 py-0" ref={glideRef}>
            <div className="glide__track" data-glide-el="track">
              <ul className={clsx('glide__slides d-flex p-0')} id={`${props?.componentId}__slides`}>
                {bestOfRestaurants?.map((item: any, _index: number) => (
                  <li>
                    <CardLayered
                      cardLayeredVariations={CardLayeredVariations.Wide}
                      eyebrow={item?.node?.cuisine || ''}
                      header={item?.node?.name}
                      headerTag={tags.h4}
                      ctaLink={`${subLocale}/dining/${item?.node?.seoUrl}.mi`}
                      badgeText=""
                      dynamicMediaWide={getDynamicMedia(item)}
                      fontSize={size.extraLarge}
                      openInNewTab={false}
                      styleclass={'inverse'}
                      trackingProperties={{
                        additionalTrackingVariables: '',
                        enableScrollingBehavior: false,
                        trackingDescription: `${item?.node?.name}`,
                        impressionTrack: false,
                        trackingContentPosition: `${CITY_PAGE}-${BEST_OF_COMPONENT}`,
                        faicMCD: '',
                        atCCeVar48: '',
                        clickTrack: true,
                        location: `${CITY_PAGE}-${BEST_OF_COMPONENT}`,
                        merchandisingCategory: 'nonCobrand',
                        impressionCount: false,
                        trackingTag: '',
                        trackingOfferType: '',
                      }}
                    />
                  </li>
                ))}
              </ul>
            </div>

            {bestOfRestaurants && bestOfRestaurants?.length > cardsPerView && (
              <div className="controls_wrapper">
                <div className="controls d-flex align-items-center justify-content-center">
                  <div className="carouselControl carouselControlType1">
                    <div className="glide__arrows" data-glide-el="controls">
                      <button
                        className={clsx('left-arrow', 'glide__arrow--left')}
                        id={`${props?.componentId}_left_arrow`}
                        data-glide-dir="<"
                      >
                        <span className="icon-arrow-left" data-id="left"></span>
                      </button>
                    </div>

                    <div data-glide-el="controls[nav]">
                      {Array.from({ length: bestOfRestaurants?.length - (cardsPerView - 1) }, (_, i) => (
                        <button
                          className=""
                          data-glide-dir={i}
                          aria-label={`page-${i + 1}`}
                          key={cardsPerView + i}
                        ></button>
                      ))}
                    </div>

                    <div className="glide__arrows" data-glide-el="controls">
                      <button
                        className={clsx('right-arrow', 'glide__arrow--right')}
                        id={`${props?.componentId}_right_arrow`}
                        data-glide-dir=">"
                      >
                        <span className="icon-arrow-right" data-id="right"></span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </StyledBestOfComponent>
  ) : null;
};

export const BestOfComponentConfig = {
  emptyLabel: 'bestofcomponent',
  isEmpty: false,
  resourceType: `${NEXT_PUBLIC_AEM_SITE}/components/content/bestofcomponent`,
};

export const BestOfComponentEditable = (props: any) => (
  <EditableComponent config={BestOfComponentConfig} {...props}>
    <BestOfComponent {...props} />
  </EditableComponent>
);
